import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Loader2, Lock, ShieldAlert, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const rules = [
  { key: 'len', label: 'Pelo menos 8 caracteres', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'Uma letra maiúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Uma letra minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'Um algarismo', test: (p) => /\d/.test(p) },
  { key: 'diff', label: 'Diferente da anterior', test: (p, cur) => p && cur && p !== cur },
];

/**
 * ChangePasswordDialog
 * - When `forced=true` the dialog is non-dismissible until password is rotated.
 * - Otherwise it can be closed via `onOpenChange`.
 */
export default function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
  const { changePassword, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrent(''); setNext(''); setConfirm(''); setSubmitting(false);
    }
  }, [open]);

  const results = rules.map((r) => ({ ...r, ok: r.test(next, current) }));
  const passesAll = results.every((r) => r.ok);
  const matches = next && next === confirm;
  const canSubmit = current && passesAll && matches && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword(current, next);
      toast.success('Palavra-passe atualizada com sucesso');
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar palavra-passe');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (forced && !v) return; // block dismissal
    onOpenChange?.(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        data-testid="change-password-dialog"
        onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
      >
        <DialogHeader className="p-6 pb-4 bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur-sm">
              {forced ? <ShieldAlert className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg font-bold text-white">
                {forced ? 'Alteração de palavra-passe obrigatória' : 'Alterar palavra-passe'}
              </DialogTitle>
              <DialogDescription className="text-white/90 mt-1 text-sm">
                {forced
                  ? 'Por segurança, defina uma nova palavra-passe antes de aceder à plataforma.'
                  : 'Defina uma nova palavra-passe para a sua conta.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pw-current">Palavra-passe atual</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="pw-current"
                type="password"
                className="pl-10"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="pw-current-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pw-new">Nova palavra-passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="pw-new"
                type="password"
                className="pl-10"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="pw-new-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pw-confirm">Confirmar nova palavra-passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="pw-confirm"
                type="password"
                className="pl-10"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="pw-confirm-input"
              />
            </div>
            {confirm && next !== confirm && (
              <p className="text-xs text-red-600" data-testid="pw-mismatch">As palavras-passe não coincidem</p>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-2">Requisitos</p>
            <ul className="space-y-1">
              {results.map((r) => (
                <li key={r.key} className={`flex items-center gap-2 text-xs ${r.ok ? 'text-emerald-700' : 'text-slate-500'}`} data-testid={`pw-rule-${r.key}`}>
                  <Check className={`h-3.5 w-3.5 ${r.ok ? 'text-emerald-600' : 'text-slate-300'}`} />
                  {r.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            {forced ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-500 hover:text-slate-700"
                data-testid="pw-logout-btn"
              >
                Terminar sessão
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange?.(false)}
                data-testid="pw-cancel-btn"
              >
                Cancelar
              </Button>
            )}
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm"
              data-testid="pw-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
              Atualizar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
